import { format, formatDistanceToNow, formatRelative } from "date-fns";
import { de, enUS } from "date-fns/locale";
import i18n from "../config";
import { isI18nEnabled } from "../feature-flag";

// Lazy-access i18n nur wenn aktiviert
const getI18n = () => {
  if (!isI18nEnabled()) return null;
  return i18n;
};

const getLocale = () => {
  if (!isI18nEnabled()) return de; // Fallback: Deutsch
  const i18n = getI18n();
  return i18n?.language === "en" ? enUS : de;
};

/**
 * Formatiert ein Datum basierend auf der aktuellen Sprache.
 *
 * @param date - Das zu formatierende Datum
 * @param formatStr - Format-String (Standard: 'PPP' = "1. Januar 2024")
 * @returns Formatierter Datums-String
 */
export function formatDate(date: Date, formatStr: string = "PPP"): string {
  return format(date, formatStr, { locale: getLocale() });
}

/**
 * Formatiert relative Zeiten (z.B. "vor 5 Minuten", "in 2 Stunden").
 *
 * @param date - Das zu formatierende Datum
 * @returns Formatierter relativer Zeit-String
 */
export function formatRelativeTime(date: Date): string {
  return formatDistanceToNow(date, {
    addSuffix: true,
    locale: getLocale(),
  });
}

/**
 * Formatiert relative Zeiten für Social-Media (z.B. "gestern", "morgen", "heute").
 *
 * @param date - Das zu formatierende Datum
 * @returns Formatierter relativer Zeit-String
 */
export function formatSocialTime(date: Date): string {
  return formatRelative(date, new Date(), { locale: getLocale() });
}

/**
 * Formatiert ein Datum im kurzen Format Tag.Monat.Jahr mit Punkten.
 * Einheitlich für DE und EN (deutsches Datumsformat).
 *
 * @param date - Das zu formatierende Datum
 * @returns Formatierter Datums-String (z. B. "11.06.2025")
 */
export function formatShortDate(date: Date): string {
  return format(date, "dd.MM.yyyy", { locale: getLocale() });
}

/**
 * Formatiert relative Zeiten für Social-Media mit Übersetzungen (z.B. "Gerade eben", "vor 5 Min", "vor 2 Std").
 * Nutzt i18n-Übersetzungen wenn verfügbar, sonst Fallback auf Deutsch.
 *
 * @param date - Das zu formatierende Datum
 * @returns Formatierter relativer Zeit-String
 */
export function formatShortRelativeTime(date: Date): string {
  const i18n = getI18n();
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  // Versuche Übersetzungen zu nutzen wenn i18n aktiviert
  if (i18n && i18n.isInitialized) {
    try {
      if (diffMins < 1) {
        return i18n.t("justNow", { ns: "social" });
      }
      if (diffMins < 60) {
        return i18n.t("minutesAgo", { ns: "social", count: diffMins });
      }
      if (diffHours < 24) {
        return i18n.t("hoursAgo", { ns: "social", count: diffHours });
      }
      if (diffDays < 7) {
        return i18n.t("daysAgo", { ns: "social", count: diffDays });
      }
    } catch {
      // Fallback zu deutschem Text
    }
  }

  // Fallback: Deutsch
  if (diffMins < 1) return "Gerade eben";
  if (diffMins < 60) return `Vor ${diffMins} Min`;
  if (diffHours < 24) return `Vor ${diffHours} Std`;
  if (diffDays < 7) return `Vor ${diffDays} Tag${diffDays > 1 ? "en" : ""}`;

  // Für längere Zeiträume: kurzes Datum
  return format(date, "dd.MM", { locale: getLocale() });
}
